import { ReactNode, useEffect, useRef } from 'react';
type LenisInstance = { raf: (t: number) => void; destroy: () => void };

interface SmoothScrollProviderProps {
  children: ReactNode;
}

const SmoothScrollProvider = ({ children }: SmoothScrollProviderProps) => {
  const lenisRef = useRef<LenisInstance | null>(null);

  useEffect(() => {
    // Skip Lenis on mobile / touch / reduced-motion devices to cut JS
    // execution time and main-thread blocking on low-end phones.
    if (typeof window === "undefined") return;
    const isMobile = window.innerWidth < 1024;
    const coarse = window.matchMedia("(pointer: coarse)").matches;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (isMobile || coarse || reduced) return;

    let rafId = 0;
    let cancelled = false;
    // Dynamic import so Lenis is never shipped to mobile bundles.
    import('lenis').then(({ default: Lenis }) => {
      if (cancelled) return;
      lenisRef.current = new Lenis({
        duration: 1.0,
        easing: (t) => 1 - Math.pow(1 - t, 3),
        orientation: 'vertical',
        gestureOrientation: 'vertical',
        smoothWheel: true,
        wheelMultiplier: 1.0,
        touchMultiplier: 1.5,
      }) as unknown as LenisInstance;
      const raf = (time: number) => {
        lenisRef.current?.raf(time);
        rafId = requestAnimationFrame(raf);
      };
      rafId = requestAnimationFrame(raf);
    });

    return () => {
      cancelled = true;
      if (rafId) cancelAnimationFrame(rafId);
      lenisRef.current?.destroy();
    };
  }, []);

  return <>{children}</>;
};

export default SmoothScrollProvider;
